import React, { useState, useEffect } from "react";
import {
  ThemeProvider,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SchoolIcon from "@mui/icons-material/School";
import { createTheme } from "@mui/material/styles";
import { getTechnicians } from "../API/technicianAPI";
import BaseWrapper from "../../components/shared/BaseWrapper";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const professional_scale = [
  "Team Leader",
  "High",
  "Mid",
  "Basic",
  "For observation",
];

const gpc_skills = [
  "rodent",
  "baiting",
  "cockroach",
  "fly_control",
  "ant_control",
  "larviciding",
];

const termite_skills = [
  "compre",
  "ag",
  "ig",
  "ic",
  "spraying",
  "soil_poisoning",
];

function ViewProfessionalSkillsMatrix() {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading

  useEffect(() => {
    (async () => {
      try {
        const fetchedTechnicians = await getTechnicians();
        setTechnicians(fetchedTechnicians);
        setLoading(false);
      } catch (error) {
        setError("Failed to fetch technicians");
        setLoading(false);
      }
    })();
  }, []);

  return (

    <BaseWrapper
      titleIcon={<SchoolIcon />}
      title="Operations"
      subTitle="Professional Skills Matrix"
      subTitleRoute={"/view-professional-skills-matrix"}
      // createButtonLabel="Add Technician"
      // onClickCreateButton={() => navigate("/add-technician")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 2,
            paddingBottom: "20px",
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ marginLeft: 2 }}>
            Loading technicians...
          </Typography>
        </Box>
      ) : error ? (
        <Typography variant="body1" color="error" sx={{ padding: 2 }}>
          {error}
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={termite_skills.length + 1}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    General Pest Control
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    Professional Level
                  </TableCell>
                </TableRow>
                <TableRow>
                  {gpc_skills.map((skill) => (
                    <TableCell
                      key={skill}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {skill
                        .replace(/_/g, " ")
                        .replace(/^\w/, (c) => c.toUpperCase())}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {professional_scale.map((level) => (
                  <TableRow
                    key={level}
                    sx={{ backgroundColor: "#fbfbfb" }}
                  >
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold" }}
                    >
                      {level}
                    </TableCell>
                    {gpc_skills.map((skill) => {
                      const matchingTechnicians = technicians
                        .filter(
                          (technician) =>
                            technician.professional_level.gpc[skill] ===
                            level
                        )
                        .map(
                          (technician) =>
                            `${technician.lastname}, ${technician.firstname}`
                        );
                      return (
                        <TableCell
                          key={skill}
                          align={
                            matchingTechnicians.length > 0
                              ? "left"
                              : "center"
                          }
                          sx={{
                            verticalAlign:
                              matchingTechnicians.length > 0
                                ? "top"
                                : "middle",
                          }}
                        >
                          {matchingTechnicians.length > 0 ? (
                            <ul style={{ textAlign: "left" }}>
                              {matchingTechnicians.map(
                                (name, index) => (
                                  <li key={index}>{name}</li>
                                )
                              )}
                            </ul>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ marginTop: 6 }} />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={termite_skills.length + 1}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    Termite
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    Professional Level
                  </TableCell>
                </TableRow>
                <TableRow>
                  {termite_skills.map((skill) => (
                    <TableCell
                      key={skill}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {skill.length === 2
                        ? skill.toUpperCase()
                        : skill
                          .replace(/_/g, " ")
                          .replace(/^\w/, (c) => c.toUpperCase())}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {professional_scale.map((level) => (
                  <TableRow
                    key={level}
                    sx={{ backgroundColor: "#fbfbfb" }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: "bold",
                      }}
                    >
                      {level}
                    </TableCell>
                    {termite_skills.map((skill) => {
                      const matchingTechnicians = technicians
                        .filter(
                          (technician) =>
                            technician.professional_level.termite[
                            skill
                            ] === level
                        )
                        .map(
                          (technician) =>
                            `${technician.lastname}, ${technician.firstname}`
                        );
                      return (
                        <TableCell
                          key={skill}
                          align={
                            matchingTechnicians.length > 0
                              ? "left"
                              : "center"
                          }
                          sx={{
                            verticalAlign:
                              matchingTechnicians.length > 0
                                ? "top"
                                : "middle",
                          }}
                        >
                          {matchingTechnicians.length > 0 ? (
                            <ul style={{ textAlign: "left" }}>
                              {matchingTechnicians.map(
                                (name, index) => (
                                  <li key={index}>{name}</li>
                                )
                              )}
                            </ul>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </BaseWrapper>
    // <ThemeProvider theme={theme}>
    //   <Box sx={{ padding: "40px 60px" }}>
    //     <Card
    //       variant="outlined"
    //       style={{
    //         marginTop: "40px",
    //         marginBottom: "40px",
    //         borderRadius: 0,
    //       }}
    //     >
    //       <CardHeader
    //         title={
    //           <Box
    //             display="flex"
    //             alignItems="center"
    //             justifyContent="space-between"
    //           >
    //             <Box display="flex" alignItems="center">
    //               <SchoolIcon
    //                 sx={{
    //                   fontSize: "40px",
    //                   marginRight: "8px",
    //                 }}
    //               />
    //               <Typography variant="h6">Operations</Typography>
    //             </Box>
    //             <Breadcrumbs
    //               aria-label="breadcrumb"
    //               separator={<NavigateNextIcon fontSize="small" />}
    //               sx={{ color: "white" }}
    //             >
    //               <Typography sx={{ color: "#ffffff99" }}>
    //                 Operations
    //               </Typography>
    //               <Typography sx={{ color: "#ffffff", textDecoration: "none" }}>
    //                 View Professional Skills Matrix
    //               </Typography>
    //             </Breadcrumbs>
    //           </Box>
    //         }
    //         sx={{
    //           backgroundColor: "#1e5d45",
    //           color: "white",
    //           padding: "16px",
    //         }}
    //       />
    //       <CardContent sx={{ padding: "20px" }}>
    //         <Card variant="outlined" sx={{ padding: "20px" }}>
    //           <CardHeader
    //             title={
    //               <Typography variant="h5" sx={{ fontWeight: "bold" }}>
    //                 Professional Skills Matrix
    //               </Typography>
    //             }
    //             sx={{ marginBottom: 2 }}
    //           />
    //           {loading ? (
    //             <Box
    //               sx={{
    //                 display: "flex",
    //                 justifyContent: "center",
    //                 alignItems: "center",
    //                 padding: 2,
    //                 paddingBottom: "20px",
    //               }}
    //             >
    //               <CircularProgress />
    //               <Typography variant="body1" sx={{ marginLeft: 2 }}>
    //                 Loading technicians...
    //               </Typography>
    //             </Box>
    //           ) : error ? (
    //             <Typography variant="body1" color="error" sx={{ padding: 2 }}>
    //               {error}
    //             </Typography>
    //           ) : (
    //             <>
    //               <TableContainer>
    //                 <Table>
    //                   <TableHead>
    //                     <TableRow>
    //                       <TableCell
    //                         colSpan={termite_skills.length + 1}
    //                         align="center"
    //                         sx={{
    //                           fontWeight: "bold",
    //                           backgroundColor: "#f5f5f5",
    //                         }}
    //                       >
    //                         General Pest Control
    //                       </TableCell>
    //                     </TableRow>
    //                     <TableRow>
    //                       <TableCell
    //                         rowSpan={2}
    //                         align="center"
    //                         sx={{
    //                           fontWeight: "bold",
    //                           backgroundColor: "#f5f5f5",
    //                         }}
    //                       >
    //                         Professional Level
    //                       </TableCell>
    //                     </TableRow>
    //                     <TableRow>
    //                       {gpc_skills.map((skill) => (
    //                         <TableCell
    //                           key={skill}
    //                           align="center"
    //                           sx={{
    //                             fontWeight: "bold",
    //                             backgroundColor: "#f5f5f5",
    //                           }}
    //                         >
    //                           {skill
    //                             .replace(/_/g, " ")
    //                             .replace(/^\w/, (c) => c.toUpperCase())}
    //                         </TableCell>
    //                       ))}
    //                     </TableRow>
    //                   </TableHead>
    //                   <TableBody>
    //                     {professional_scale.map((level) => (
    //                       <TableRow
    //                         key={level}
    //                         sx={{ backgroundColor: "#fbfbfb" }}
    //                       >
    //                         <TableCell
    //                           align="center"
    //                           sx={{ fontWeight: "bold" }}
    //                         >
    //                           {level}
    //                         </TableCell>
    //                         {gpc_skills.map((skill) => {
    //                           const matchingTechnicians = technicians
    //                             .filter(
    //                               (technician) =>
    //                                 technician.professional_level.gpc[skill] ===
    //                                 level
    //                             )
    //                             .map(
    //                               (technician) =>
    //                                 `${technician.lastname}, ${technician.firstname}`
    //                             );
    //                           return (
    //                             <TableCell
    //                               key={skill}
    //                               align={
    //                                 matchingTechnicians.length > 0
    //                                   ? "left"
    //                                   : "center"
    //                               }
    //                               sx={{
    //                                 verticalAlign:
    //                                   matchingTechnicians.length > 0
    //                                     ? "top"
    //                                     : "middle",
    //                               }}
    //                             >
    //                               {matchingTechnicians.length > 0 ? (
    //                                 <ul style={{ textAlign: "left" }}>
    //                                   {matchingTechnicians.map(
    //                                     (name, index) => (
    //                                       <li key={index}>{name}</li>
    //                                     )
    //                                   )}
    //                                 </ul>
    //                               ) : (
    //                                 "N/A"
    //                               )}
    //                             </TableCell>
    //                           );
    //                         })}
    //                       </TableRow>
    //                     ))}
    //                   </TableBody>
    //                 </Table>
    //               </TableContainer>

    //               <Box sx={{ marginTop: 6 }} />

    //               <TableContainer>
    //                 <Table>
    //                   <TableHead>
    //                     <TableRow>
    //                       <TableCell
    //                         colSpan={termite_skills.length + 1}
    //                         align="center"
    //                         sx={{
    //                           fontWeight: "bold",
    //                           backgroundColor: "#f5f5f5",
    //                         }}
    //                       >
    //                         Termite
    //                       </TableCell>
    //                     </TableRow>
    //                     <TableRow>
    //                       <TableCell
    //                         rowSpan={2}
    //                         align="center"
    //                         sx={{
    //                           fontWeight: "bold",
    //                           backgroundColor: "#f5f5f5",
    //                         }}
    //                       >
    //                         Professional Level
    //                       </TableCell>
    //                     </TableRow>
    //                     <TableRow>
    //                       {termite_skills.map((skill) => (
    //                         <TableCell
    //                           key={skill}
    //                           align="center"
    //                           sx={{
    //                             fontWeight: "bold",
    //                             backgroundColor: "#f5f5f5",
    //                           }}
    //                         >
    //                           {skill.length === 2
    //                             ? skill.toUpperCase()
    //                             : skill
    //                                 .replace(/_/g, " ")
    //                                 .replace(/^\w/, (c) => c.toUpperCase())}
    //                         </TableCell>
    //                       ))}
    //                     </TableRow>
    //                   </TableHead>
    //                   <TableBody>
    //                     {professional_scale.map((level) => (
    //                       <TableRow
    //                         key={level}
    //                         sx={{ backgroundColor: "#fbfbfb" }}
    //                       >
    //                         <TableCell
    //                           align="center"
    //                           sx={{
    //                             fontWeight: "bold",
    //                           }}
    //                         >
    //                           {level}
    //                         </TableCell>
    //                         {termite_skills.map((skill) => {
    //                           const matchingTechnicians = technicians
    //                             .filter(
    //                               (technician) =>
    //                                 technician.professional_level.termite[
    //                                   skill
    //                                 ] === level
    //                             )
    //                             .map(
    //                               (technician) =>
    //                                 `${technician.lastname}, ${technician.firstname}`
    //                             );
    //                           return (
    //                             <TableCell
    //                               key={skill}
    //                               align={
    //                                 matchingTechnicians.length > 0
    //                                   ? "left"
    //                                   : "center"
    //                               }
    //                               sx={{
    //                                 verticalAlign:
    //                                   matchingTechnicians.length > 0
    //                                     ? "top"
    //                                     : "middle",
    //                               }}
    //                             >
    //                               {matchingTechnicians.length > 0 ? (
    //                                 <ul style={{ textAlign: "left" }}>
    //                                   {matchingTechnicians.map(
    //                                     (name, index) => (
    //                                       <li key={index}>{name}</li>
    //                                     )
    //                                   )}
    //                                 </ul>
    //                               ) : (
    //                                 "N/A"
    //                               )}
    //                             </TableCell>
    //                           );
    //                         })}
    //                       </TableRow>
    //                     ))}
    //                   </TableBody>
    //                 </Table>
    //               </TableContainer>
    //             </>
    //           )}
    //         </Card>
    //       </CardContent>
    //     </Card>
    //   </Box>
    // </ThemeProvider>
  );
}

export default ViewProfessionalSkillsMatrix;
